/**
 * Per-clinic utilization tracker — sits above the OD Bonus panel on each
 * clinic's tab. Shows current-month shows vs. target, live utilization %,
 * MoM delta, and a coloured status bar (strong / warn / danger).
 *
 * Data is served by GET /api/clinics/{clinic_id}/bonus which now includes a
 * `utilization` block sourced from the BI cube (bi_snapshot.utilization).
 * We piggyback on that endpoint so we don't add another network round-trip
 * to the clinic-view render path.
 */
import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Card } from '../ui/card';
import { Activity, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, ReferenceLine, Cell } from 'recharts';
import BonusReachCallout from '../BonusReachCallout';
import InfoTooltip from '../InfoTooltip';

const C = {
  ink: '#2D2833',
  soft: '#685B72',
  border: '#E8E5EC',
  strong: '#839E97',
  warn: '#B08A3A',
  danger: '#8A3A3A',
};

const STATUS_STYLES = {
  strong: { color: C.strong, label: 'On pace' },
  warn:   { color: C.warn,   label: 'Below target' },
  danger: { color: C.danger, label: 'Well below target' },
};

const NUM = (n) => (n == null ? '—' : Number(n).toLocaleString());
const PCT = (n, d = 1) => (n == null ? '—' : `${(Number(n) * 100).toFixed(d)}%`);

export default function ClinicUtilizationPanel({ clinicId, clinicName }) {
  const [util, setUtil] = useState(null);

  useEffect(() => {
    if (!clinicId) return;
    api.get(`/clinics/${clinicId}/bonus`)
      .then(r => setUtil(r.data?.utilization || null))
      .catch(() => setUtil(null));
  }, [clinicId]);

  if (!util || util.utilization == null) return null;  // no BI data yet — hide silently

  const util_pct = Number(util.utilization);
  const shows = util.shows ?? 0;
  const fullTarget = util.monthly_target || util.target || 0;
  const target = util.elapsed_target || fullTarget;   // prorated (business days elapsed)
  const bdElapsed = util.biz_days_elapsed;
  const bdTotal = util.biz_days_in_month;
  const barPct = Math.min(100, Math.max(0, util_pct * 100));
  const status = STATUS_STYLES[util.status] || STATUS_STYLES.warn;
  const monLabel = util.month ? `${util.month} ${util.year || ''}`.trim() : 'Current month';
  const mom = util.mom_delta;

  return (
    <Card
      className="rounded-2xl border p-6 bg-white"
      style={{ borderColor: C.border }}
      data-testid="clinic-utilization-panel"
    >
      <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: status.color }}>
            <Activity size={12} strokeWidth={2.4} /> Utilization · {monLabel}
          </div>
          <h3 className="font-serif text-2xl mt-1 flex items-center" style={{ color: C.ink }}>
            {clinicName} — utilization<InfoTooltip testId="tt-utilization" text={`Utilization % = counted visits ÷ prorated target through today. Target is prorated by business days elapsed this month (${bdElapsed ?? '—'} of ${bdTotal ?? '—'} bd × ${fullTarget || '—'}). Counted visits = Show + No Show + Late Cancel. Green ≥ 90%, amber 70–89%, red < 70%.`} />
          </h3>
          <div className="text-[12px] mt-1" style={{ color: C.soft }}>
            {NUM(shows)} shows · pace target {NUM(target)}{bdElapsed != null && bdTotal ? ` (${bdElapsed}/${bdTotal} biz days · full-month ${NUM(fullTarget)})` : ''} · min expected {PCT(util.min_expected_pct)}
          </div>
        </div>
        <div className="text-right">
          <div className="font-serif tabular-nums leading-none" style={{ color: status.color, fontSize: 40 }}>
            {Math.round(util_pct * 100)}<span className="text-[20px]" style={{ color: C.soft }}>%</span>
          </div>
          <div className="text-[11px] mt-1 font-semibold uppercase tracking-widest" style={{ color: status.color }}>
            {status.label}
          </div>
        </div>
      </div>

      {/* Utilization bar */}
      <div className="h-3 w-full rounded-full overflow-hidden" style={{ background: '#F7EFDA' }}>
        <div
          className="h-3 transition-all"
          style={{
            width: `${barPct}%`,
            background: status.color,
          }}
          data-testid="utilization-bar"
        />
      </div>

      {/* Bonus Reach — quarterly catch-up math when clinic is in 70–80% band */}
      {util.bonus_reach && (
        <div className="mt-4">
          <BonusReachCallout reach={util.bonus_reach} clinicName={clinicName} />
        </div>
      )}

      {/* Trailing metrics */}
      <div className="grid grid-cols-3 gap-4 mt-5">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Shows</div>
          <div className="font-serif text-xl mt-1" style={{ color: C.ink }}>{NUM(shows)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Pace target</div>
          <div className="font-serif text-xl mt-1" style={{ color: C.ink }}>{NUM(target)}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>MoM</div>
          <div className="font-serif text-xl mt-1 inline-flex items-center gap-1" style={{ color: mom == null ? C.soft : mom > 0 ? C.strong : mom < 0 ? C.danger : C.soft }}>
            {mom == null ? '—' : (
              <>
                {mom > 0 ? <ArrowUpRight size={16} strokeWidth={2.4} /> : mom < 0 ? <ArrowDownRight size={16} strokeWidth={2.4} /> : null}
                {(Math.abs(mom) * 100).toFixed(1)}%
              </>
            )}
          </div>
        </div>
      </div>

      {/* 12-month utilization tracker (iter 47) — bar chart across the
          rolling window, oldest → current. Data points with zero shows are
          filtered out so the chart never carries dead months. Threshold
          line = min-expected utilization. */}
      {Array.isArray(util.history) && util.history.filter(h => (h.shows || 0) > 0).length > 1 && (
        <div className="mt-6 pt-5 border-t" style={{ borderColor: C.border }} data-testid="utilization-12mo-trend">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: C.soft }}>
              12-month utilization tracker
            </div>
            <div className="text-[10px]" style={{ color: C.soft }}>
              Min expected {PCT(util.min_expected_pct, 0)}
            </div>
          </div>
          <div style={{ width: '100%', height: 170, minHeight: 170 }}>
            <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
              <BarChart
                data={util.history
                  .filter(h => (h.shows || 0) > 0)
                  .map(h => ({
                    label: h.label,
                    pct: Math.round((h.utilization || 0) * 1000) / 10,
                    shows: h.shows,
                  }))}
                margin={{ top: 8, right: 12, left: 0, bottom: 4 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#F1EDF5" vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.soft }} interval={0} />
                <YAxis
                  domain={[0, 120]}
                  tick={{ fontSize: 10, fill: C.soft }}
                  unit="%"
                  width={38}
                />
                <Tooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8, border: `1px solid ${C.border}` }}
                  formatter={(v, name, props) => name === 'pct'
                    ? [`${v}%`, `Utilization (${props?.payload?.shows ?? 0} shows)`]
                    : [v, 'Shows']}
                />
                <ReferenceLine
                  y={Math.round((util.min_expected_pct || 0.90) * 100)}
                  stroke={C.warn}
                  strokeDasharray="4 3"
                  label={{ value: 'Min', fontSize: 9, fill: C.warn, position: 'right' }}
                />
                <Bar dataKey="pct" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                  {util.history.filter(h => (h.shows || 0) > 0).map((h, i) => {
                    const pct = h.utilization || 0;
                    const fill = pct >= (util.min_expected_pct || 0.90) ? C.strong : pct >= 0.75 ? C.warn : C.danger;
                    return <Cell key={`bar-${i}`} fill={fill} />;
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </Card>
  );
}
